/**
 * T&M Log Helper Functions
 * Shared utility functions for T&M log operations
 */

import { parseNonZeroNumber } from "../../../utils/common/formatting/numbers";

/**
 * Get the most recent inspector from a list of inspectors
 * @param {Array} inspectors - Array of inspector objects with sent_at timestamps
 * @returns {Object|null} The most recent inspector or null if none exist
 */
export const getLatestInspector = (inspectors) => {
  if (!inspectors || inspectors.length === 0) {
    return null;
  }

  return [...inspectors].sort(
    (a, b) => new Date(b.sent_at) - new Date(a.sent_at)
  )[0];
};

/**
 * Resolve the display amount for a T&M report row.
 *
 * Prefers TMPrice.total_cost (fully priced) over backend estimated_cost
 * (raw sum of report line items). Zero amounts are treated as empty so
 * the UI can show a dash instead of $0.00.
 *
 * @param {Object} report - T&M report (or project-summary row) from the API.
 * @returns {{
 *   value: number|null,
 *   source: ('priced'|'estimated'|null),
 *   tooltip: string,
 *   textClassName: string,
 * }} Amount display metadata for the Amount column.
 */
export function getTMAmountDisplay(report) {
  const pricedValue = parseNonZeroNumber(report?.pricing?.total_cost);
  if (pricedValue != null) {
    return {
      value: pricedValue,
      source: "priced",
      tooltip: "Priced",
      textClassName: "text-green-700",
    };
  }

  // Priced row exists but total is $0 — show dash
  if (report?.pricing != null) {
    return {
      value: null,
      source: "priced",
      tooltip: "Not Enough Data",
      textClassName: "text-green-700",
    };
  }

  const estimatedValue = parseNonZeroNumber(report?.estimated_cost);
  if (estimatedValue != null) {
    return {
      value: estimatedValue,
      source: "estimated",
      tooltip: "Estimated",
      textClassName: "text-gray-500",
    };
  }

  return {
    value: null,
    source: null,
    tooltip: "Not Enough Data",
    textClassName: "text-gray-500",
  };
}

/**
 * Numeric amount used for sorting and combined tile totals.
 * Prefers priced total_cost, else estimated_cost, else 0.
 *
 * @param {Object} report - T&M report row from the API.
 * @returns {number} Amount to include in sums/sorts.
 */
export function getTMAmountValue(report) {
  if (report?.pricing?.total_cost != null && report?.pricing?.total_cost !== "") {
    return Number(report.pricing.total_cost) || 0;
  }
  return Number(report?.estimated_cost) || 0;
}

/**
 * Sum estimated and fully priced totals for a list of reports.
 *
 * Estimated uses each report's estimated_cost; priced uses pricing.total_cost
 * when a TMPrice exists (else 0 for that row).
 *
 * @param {Array<Object>} reports - Filtered T&M report rows.
 * @returns {{ estimated: number, priced: number }} Separate estimated and priced sums.
 */
export function getTMAmountTotals(reports) {
  return (reports || []).reduce(
    (totals, report) => {
      totals.estimated += Number(report?.estimated_cost) || 0;
      totals.priced += Number(report?.pricing?.total_cost) || 0;
      return totals;
    },
    { estimated: 0, priced: 0 }
  );
}
